import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Search } from './../../../../Common/Tables/Search'
//import { InstructorBase } from './../../../../Layouts/Instructor/InstructorBase'
import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { Details } from './../../../../Common/Modals/Details'
import { Confirm } from './../../../../Common/Modals/Confirm'
import { GetAllDataGroups, DeleteGroup as BLDeleteGroup, HideGroup as BLHideGroup, ShowGroup as BLShowGroup, RenameGroup as BLRenameGroup } from './../../../../../utilities/BL/Instructor/Groups'
import { ResetGroupToShow, SaveGroupToShow } from './../../../../../utilities/BL/Navigation'
import { Loading } from './../../../../Common/Loading'

import { LogErrors } from './../../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'
import { HELP_TYPE } from './../../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../../utilities/EL/Roles'
//import { HELP_TYPE } from './../../../../Header/Header'
//import { useTawkChat } from './../../../../../Support/Chat/Chat'



// Groups.js
// Componente para administrar grupos (visibles / ocultos) con:
// - Búsqueda y filtros
// - Acciones masivas (ocultar, mostrar, eliminar)
// - Acciones individuales
// - Edición del nombre de grupo con doble modal (edición + confirmación)

const Groups = () => {
  const navigate = useNavigate()
  const { showMessage } = useFloatMessage()

  // -------------------------------------------------
  // Estado general de la página
  // -------------------------------------------------
  const [isLoading, setIsLoading] = useState(true)

  // -------------------------------------------------
  // Modales de confirmación (eliminar / ocultar / mostrar / renombrar)
  // -------------------------------------------------
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [actionTypeOfModal, setActionTypeOfModal] = useState(null)

  /**
   * groupConfirmDetails:
   * - Para DELETE/HIDE/SHOW: datos del grupo (id, name, etc.)
   * - Para RENAME: además incluye newName (nombre nuevo propuesto)
   */
  const [groupConfirmDetails, setGroupConfirmDetails] = useState(null)

  // -------------------------------------------------
  // Resumen de acciones masivas (ya implementado)
  // -------------------------------------------------
  const [massiveSummary, setMassiveSummary] = useState(null)
  const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)

  // -------------------------------------------------
  // Listas base (fuente de verdad)
  // -------------------------------------------------
  const [allRows, setAllRows] = useState([]) // grupos visibles
  const [hiddenRows, setHiddenRows] = useState([]) // grupos ocultos

  // -------------------------------------------------
  // Listas filtradas para la UI
  // -------------------------------------------------
  const [filteredRows, setFilteredRows] = useState([]) // visibles filtrados por buscador
  const [filteredHiddenRows, setFilteredHiddenRows] = useState([]) // ocultos filtrados

  // -------------------------------------------------
  // Cajas de búsqueda (texto)
  // -------------------------------------------------
  const [searchTextVisible, setSearchTextVisible] = useState('')
  const [searchTextHidden, setSearchTextHidden] = useState('')

  // -------------------------------------------------
  // Selecciones masivas (IDs de grupos seleccionados)
  // -------------------------------------------------
  const [selectedVisibleGroupIds, setSelectedVisibleGroupIds] = useState([])
  const [selectedHiddenGroupIds, setSelectedHiddenGroupIds] = useState([])

  // -------------------------------------------------
  // Refs para mantener listas actuales durante iteraciones asíncronas
  // -------------------------------------------------
  const allRowsRef = useRef([])
  const hiddenRowsRef = useRef([])
  const allGroupsRef = useRef([])

  useEffect(() => {
    allRowsRef.current = allRows
  }, [allRows])

  useEffect(() => {
    hiddenRowsRef.current = hiddenRows
  }, [hiddenRows])

  useEffect(() => {
    allGroupsRef.current = [...allRows, ...hiddenRows]
  }, [allRows, hiddenRows])

  // -------------------------------------------------
  // Filtrado reactivo de grupos visibles
  // -------------------------------------------------
  useEffect(() => {
    const txt = searchTextVisible.trim().toLowerCase()
    const filtered = txt
      ? allRows.filter((row) => row.name.toLowerCase().includes(txt))
      : allRows
    setFilteredRows(filtered)
  }, [searchTextVisible, allRows])

  // -------------------------------------------------
  // Filtrado reactivo de grupos ocultos
  // -------------------------------------------------
  useEffect(() => {
    const txt = searchTextHidden.trim().toLowerCase()
    const filtered = txt
      ? hiddenRows.filter((row) => row.name.toLowerCase().includes(txt))
      : hiddenRows
    setFilteredHiddenRows(filtered)
  }, [searchTextHidden, hiddenRows])

  // -------------------------------------------------
  // Contadores (totales sin filtro)
  // -------------------------------------------------
  const visibleTotal = allRows.length
  const hiddenTotal = hiddenRows.length

  // -------------------------------------------------
  // Tipos de acción
  // -------------------------------------------------
  const actionType = {
    HIDE: 'HIDE',
    SHOW: 'SHOW',
    DELETE: 'DELETE',
    RENAME: 'RENAME'
  }

  // -------------------------------------------------
  // Helpers selección (regresan filas completas seleccionadas)
  // -------------------------------------------------
  const GetSelectedVisibleGroups = () =>
    allRowsRef.current.filter((row) => selectedVisibleGroupIds.includes(row.id))

  const GetSelectedHiddenGroups = () =>
    hiddenRowsRef.current.filter((row) => selectedHiddenGroupIds.includes(row.id))

  // -------------------------------------------------
  // Utils comunes
  // -------------------------------------------------
  const ClearSelections = async () => {
    try {
      setSelectedVisibleGroupIds([])
      setSelectedHiddenGroupIds([])
    } catch (error) {
      await LogErrors('ClearSelections - Groups', error)
    }
  }

  /**
   * Fusiona listas visibles/ocultas actualizadas dentro de una "all" (sin duplicar ni perder)
   * @param {Array} currentRows Lista completa actual (visibles + ocultos)
   * @param {Array} updatedVisible Lista actualizada de visibles
   * @param {Array} updatedHidden Lista actualizada de ocultos
   * @returns {Array} Nueva lista completa fusionada
   */
  const MergeUpdatedRows = async (currentRows, updatedVisible, updatedHidden) => {
    try {
      const map = new Map()
      // Indexar actuales
      currentRows.forEach((row) => map.set(row.id, row))
      // Sobrescribir con visibles nuevos
      ;(updatedVisible || []).forEach((row) => map.set(row.id, row))
      // Sobrescribir con ocultos nuevos
      ;(updatedHidden || []).forEach((row) => map.set(row.id, row))
      return Array.from(map.values())
    } catch (error) {
      await LogErrors('MergeUpdatedRows - Groups', error)
    }
  }

  /**
   * Ejecución genérica para acciones masivas (ocultar/mostrar/eliminar) sobre grupos seleccionados.
   * Usa summary para mostrar posteriormente un resumen en un modal.
   */
  const HandleMassiveAction = async ({ selectedRows, actionFn, actionLabel }) => {
    try {
      const summary = { successful: [], wrongs: [], omitteds: [] }
      let currentAll = [...allGroupsRef.current] // copia completa (visibles+ocultos)

      for (const row of selectedRows) {
        // Verificamos si el grupo sigue existiendo en la lista actual
        const stillExists = currentAll.find((r) => r.id === row.id)
        if (!stillExists) {
          summary.omitteds.push({ ...row, reason: 'Ya no existe en la lista actual' })
          continue
        }

        try {
          const visibleRowsList = currentAll.filter((z) => z.visible)
          const hiddenRowsList = currentAll.filter((z) => !z.visible)

          const response = await actionFn(row, visibleRowsList, hiddenRowsList)
          if (response.success) {
            summary.successful.push(row)

            // Soportar tanto formato data: [] como data: { updatedVisible, updatedHidden }
            if (response.data?.updatedVisible && response.data?.updatedHidden) {
              currentAll = await MergeUpdatedRows(
                currentAll,
                response.data.updatedVisible,
                response.data.updatedHidden
              )
            } else if (Array.isArray(response.data)) {
              currentAll = response.data
            }
          } else {
            summary.wrongs.push({ ...row, error: response.message ?? 'Error desconocido' })
          }
        } catch (error) {
          summary.wrongs.push({ ...row, error: error.message ?? 'Excepción desconocida' })
        }
      }

      // Actualizar estados globales a partir de currentAll
      const visibles = currentAll.filter((z) => z.visible)
      const ocultos = currentAll.filter((z) => !z.visible)

      setAllRows(visibles)
      setHiddenRows(ocultos)

      // Reaplicar filtros actuales
      setFilteredRows(
        searchTextVisible
          ? visibles.filter((r) =>
              r.name.toLowerCase().includes(searchTextVisible.toLowerCase())
            )
          : visibles
      )
      setFilteredHiddenRows(
        searchTextHidden
          ? ocultos.filter((r) =>
              r.name.toLowerCase().includes(searchTextHidden.toLowerCase())
            )
          : ocultos
      )

      setMassiveSummary(summary)
      setActionTypeOfModal(actionLabel)
      setShowMassiveSummaryModal(true)

      await ClearSelections()
    } catch (error) {
      await LogErrors('HandleMassiveAction - Groups', error)
    }
  }

  // -------------------------------------------------
  // Acciones unitarias ya existentes
  // -------------------------------------------------
  const DeleteGroup = async () => {
    try {
      if (!groupConfirmDetails) return
      const all = [...allRowsRef.current, ...hiddenRowsRef.current]
      const response = await BLDeleteGroup(groupConfirmDetails, all)

      if (response.success) {
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: `El grupo ${groupConfirmDetails.name} ha sido eliminado exitosamente.`
        })

        const updated = response.data
        const visibles = updated.filter((z) => z.visible)
        const ocultos = updated.filter((z) => !z.visible)

        setAllRows(visibles)
        setHiddenRows(ocultos)
        setFilteredRows(
          searchTextVisible
            ? visibles.filter((r) =>
                r.name.toLowerCase().includes(searchTextVisible.toLowerCase())
              )
            : visibles
        )
        setFilteredHiddenRows(
          searchTextHidden
            ? ocultos.filter((r) =>
                r.name.toLowerCase().includes(searchTextHidden.toLowerCase())
              )
            : ocultos
        )
      }
      return response
    } catch (error) {
      await LogErrors('DeleteGroup - Groups', error)
    }
  }

  const HideGroup = async () => {
    try {
      if (!groupConfirmDetails) return
      const response = await BLHideGroup(
        groupConfirmDetails,
        allRowsRef.current,
        hiddenRowsRef.current
      )

      if (response.success) {
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: `El grupo ${groupConfirmDetails.name} ha sido ocultado exitosamente.`
        })

        const visibles = response.data.updatedVisible || []
        const ocultos = response.data.updatedHidden || []

        setAllRows(visibles)
        setHiddenRows(ocultos)
        setFilteredRows(
          searchTextVisible
            ? visibles.filter((r) =>
                r.name.toLowerCase().includes(searchTextVisible.toLowerCase())
              )
            : visibles
        )
        setFilteredHiddenRows(
          searchTextHidden
            ? ocultos.filter((r) =>
                r.name.toLowerCase().includes(searchTextHidden.toLowerCase())
              )
            : ocultos
        )
      }
      return response
    } catch (error) {
      await LogErrors('HideGroup - Groups', error)
    }
  }

  const ShowGroup = async () => {
    try {
      if (!groupConfirmDetails) return
      const response = await BLShowGroup(
        groupConfirmDetails,
        allRowsRef.current,
        hiddenRowsRef.current
      )

      if (response.success) {
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: `El grupo ${groupConfirmDetails.name} ha sido mostrado exitosamente.`
        })

        const visibles = response.data.updatedVisible || []
        const ocultos = response.data.updatedHidden || []

        setAllRows(visibles)
        setHiddenRows(ocultos)
        setFilteredRows(
          searchTextVisible
            ? visibles.filter((r) =>
                r.name.toLowerCase().includes(searchTextVisible.toLowerCase())
              )
            : visibles
        )
        setFilteredHiddenRows(
          searchTextHidden
            ? ocultos.filter((r) =>
                r.name.toLowerCase().includes(searchTextHidden.toLowerCase())
              )
            : ocultos
        )
      }
      return response
    } catch (error) {
      await LogErrors('ShowGroup - Groups', error)
    }
  }

  // -------------------------------------------------
  // NUEVO: Estado y lógica para edición de nombre de grupo
  // -------------------------------------------------
  const [showEditModal, setShowEditModal] = useState(false) // muestra/oculta modal de edición
  const [editingGroup, setEditingGroup] = useState(null) // grupo que se está editando
  const [editingName, setEditingName] = useState('') // texto del nuevo nombre

  /**
   * Abre el modal de edición de nombre para el grupo indicado.
   * @param {Object} group Grupo a editar (id, name, visible, etc.)
   */
  const OpenEditGroupModal = async (group) => {
    try {
      if (!group) return
      setEditingGroup(group)
      setEditingName(group.name || '')
      setShowEditModal(true)
    } catch (error) {
      await LogErrors('OpenEditGroupModal - Groups', error)
    }
  }

  /**
   * Ejecuta el renombrado del grupo después de la confirmación.
   * Llama a BLRenameGroup(idGroup, newGroupName) y actualiza las listas locales
   * sin afectar el resto de la lógica ya existente.
   */
  const RenameGroup = async () => {
    try {
      if (!groupConfirmDetails) return

      const newName = (groupConfirmDetails.newName || '').trim()
      if (!newName) return

      // Llamada al BL con la firma que nos compartiste: RenameGroup(idGroup, newGroupName)
      const response = await BLRenameGroup(groupConfirmDetails.id, newName)

      if (response.success) {
        // Mensaje de éxito
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: `El grupo ${groupConfirmDetails.name} ha sido renombrado a ${newName}.`
        })

        // Actualizamos las listas locales de la manera más segura posible,
        // soportando distintos posibles formatos de respuesta.

        let updatedAll = [...allGroupsRef.current]

        // 1) Si el backend regresa lista completa (visibles+ocultos) → usamos esa
        if (Array.isArray(response.data)) {
          updatedAll = response.data
        }
        // 2) Si regresa formato { updatedVisible, updatedHidden } → reconstruimos desde ahí
        else if (response.data?.updatedVisible || response.data?.updatedHidden) {
          const visibles = response.data.updatedVisible || []
          const ocultos = response.data.updatedHidden || []
          updatedAll = [...visibles, ...ocultos]
        }
        // 3) Si regresa solo el grupo actualizado → lo reemplazamos por id en la lista actual
        else if (response.data?.idGroup || response.data?.id) {
          const updatedGroupId = response.data.idGroup ?? response.data.id
          const updatedGroupName =
            response.data.groupName ?? response.data.name ?? newName

          updatedAll = updatedAll.map((g) =>
            g.id === updatedGroupId
              ? { ...g, name: updatedGroupName }
              : g
          )
        } else {
          // Si por alguna razón no reconocemos el formato,
          // al menos actualizamos el nombre localmente:
          updatedAll = updatedAll.map((g) =>
            g.id === groupConfirmDetails.id
              ? { ...g, name: newName }
              : g
          )
        }

        const visibles = updatedAll.filter((z) => z.visible)
        const ocultos = updatedAll.filter((z) => !z.visible)

        setAllRows(visibles)
        setHiddenRows(ocultos)

        // Reaplicar filtros actuales
        setFilteredRows(
          searchTextVisible
            ? visibles.filter((r) =>
                r.name.toLowerCase().includes(searchTextVisible.toLowerCase())
              )
            : visibles
        )

        setFilteredHiddenRows(
          searchTextHidden
            ? ocultos.filter((r) =>
                r.name.toLowerCase().includes(searchTextHidden.toLowerCase())
              )
            : ocultos
        )
      }

      return response
    } catch (error) {
      await LogErrors('RenameGroup - Groups', error)
    }
  }

  // -------------------------------------------------
  // Modal de confirmación genérico (DELETE / HIDE / SHOW / RENAME)
  // -------------------------------------------------
  const ShowConfirmModal = async (_actionType, group) => {
    try {
      if (_actionType && group) {
        if (
          _actionType === actionType.DELETE ||
          _actionType === actionType.HIDE ||
          _actionType === actionType.SHOW ||
          _actionType === actionType.RENAME
        ) {
          setActionTypeOfModal(_actionType)
          setGroupConfirmDetails(group)
          setShowConfirmationModal(true)
        } else {
          setActionTypeOfModal(null)
          setGroupConfirmDetails(null)
          setShowConfirmationModal(false)
          return
        }
      }
    } catch (error) {
      await LogErrors('ShowConfirmModal - Groups', error)
    }
  }

  // -------------------------------------------------
  // Navegar a detalles del grupo
  // -------------------------------------------------
  const ShowGroupDetails = async (group) => {
    try {
      const response = await SaveGroupToShow(group.id, group.name)
      if (response.success) navigate('/instructor/groups/group/')
    } catch {}
  }

  // -------------------------------------------------
  // Carga inicial
  // -------------------------------------------------
  const LoadPage = async () => {
    try {
      const response = await GetAllDataGroups()

      if (response.success) {
        const visibles = response.data.visibles.map((g) => ({
          id: g.idGroup,
          name: g.groupName,
          accessCode: g.groupCode,
          ztudiantesQuantity: g.ztudiantesCount,
          visible: true
        }))

        const noVisibles = response.data.noVisibles.map((g) => ({
          id: g.idGroup,
          name: g.groupName,
          accessCode: g.groupCode,
          ztudiantesQuantity: g.ztudiantesCount,
          visible: false
        }))

        setAllRows(visibles)
        setHiddenRows(noVisibles)
        setFilteredRows(visibles)
        setFilteredHiddenRows(noVisibles)
      }

      await ResetGroupToShow()
    } catch (error) {
      await LogErrors('LoadPage - Groups', error)
    } finally {
      setIsLoading(false)
    }
  }

  // -------------------------------------------------
  // Validación de sesión y carga inicial de datos
  // -------------------------------------------------
  useEffect(() => {
    async function Validate() {
      const tmpSession = await ValidateSession()

      if (tmpSession.success) {
        if (tmpSession.data.user.userStatus === 2) {
          await LoadPage()
        } else {
          navigate('/confirm')
        }
      } else {
        navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
      }
    }
    Validate()
  }, []) // eslint-disable-line

  // useTawkChat(true) // Ejemplo para activar chat aquí

  // -------------------------------------------------
  // RENDER
  // -------------------------------------------------
  return isLoading ? (
    <Loading />
  ) : (
    <>
      <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.INSTRUCTOR.GROUPS} page={WEBPAGES.INSTRUCTOR.GROUPS} role={ROLES.INSTRUCTOR}>
        <section className='container Instructor__Groups'>
          <p className='Instructor__Title Instructor__Title--Center'>Mis Grupos</p>

          {/* ======== VISIBLES ======== */}
          {visibleTotal > 0 && (
            <p className='Instructor__Subtitle Instructor__Subtitle--Center'>
              Total: {visibleTotal}
            </p>
          )}

          <button
            type='button'
            className='Btn__Ztudia--PrimaryOutline Instructor__SpaceUp Instructor__CreateButton'
            onClick={() => navigate('/instructor/groups/create')}
          >
            Crear grupo
          </button>

          {Array.isArray(filteredRows) ? (
            <>
              {visibleTotal === 0 ? (
                <p className='Instructor__Text--Center Instructor__SpaceUp'>
                  Actualmente no tienes grupos.
                </p>
              ) : (
                <>
                  {/* Buscador visibles */}
                  <Search
                    onChange={(v) => setSearchTextVisible(v)}
                    extraClassName='Instructor__SpaceUp'
                  />

                  <div className='container Instructor__SpaceUp Tables--Shadow Tables--Background'>
                    {/* Acciones masivas (visibles) */}
                    {selectedVisibleGroupIds.length > 0 && (
                      <div className='Tables__ExtraButtons'>
                        <button
                          className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                          onClick={async () => {
                            await HandleMassiveAction({
                              selectedRows: GetSelectedVisibleGroups(),
                              actionFn: (group, rows) =>
                                BLHideGroup(group, rows, hiddenRowsRef.current),
                              actionLabel: actionType.HIDE
                            })
                          }}
                        >
                          Ocultar {selectedVisibleGroupIds.length} grupo(s)
                        </button>

                        <button
                          className='Btn__Ztudia--DangerOutline Tables__ExtraButtons__Button'
                          onClick={async () => {
                            await HandleMassiveAction({
                              selectedRows: GetSelectedVisibleGroups(),
                              actionFn: (group, rows) => BLDeleteGroup(group, rows),
                              actionLabel: actionType.DELETE
                            })
                          }}
                        >
                          Eliminar {selectedVisibleGroupIds.length} grupo(s)
                        </button>
                      </div>
                    )}

                    {/* Franja de registros seleccionados (visibles) */}
                    {selectedVisibleGroupIds.length > 0 && (
                      <div className='Tables__Table__RegistersSelected'>
                        <button
                          className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm'
                          onClick={() => setSelectedVisibleGroupIds([])}
                        >
                          <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                          <span className='Tables__Table__RegistersSelected__Button__Text'>
                            Cerrar
                          </span>
                        </button>

                        <div className='Tables__Table__RegistersSelected__Text'>
                          {selectedVisibleGroupIds.length} grupo(s) seleccionado(s)
                        </div>

                        <button
                          className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg'
                          onClick={() => setSelectedVisibleGroupIds([])}
                        >
                          <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                          <span className='Tables__Table__RegistersSelected__Button__Text'>
                            Cerrar
                          </span>
                        </button>
                      </div>
                    )}

                    {/* Tabla visibles */}
                    <div className='Tables__Container'>
                      <table className='Tables__Table Instructor__Groups__Visible__ContainerTable__Table'>
                        {selectedVisibleGroupIds.length === 0 && (
                          <thead>
                            <tr className='Tables__Table__Row--Header'>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Groups__Visible__ContainerTable__Table__InputColumn'>
                                <input
                                  type='checkbox'
                                  className='Tables__Table__InputColumn__Input'
                                  checked={
                                    filteredRows.length > 0 &&
                                    selectedVisibleGroupIds.length === filteredRows.length
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedVisibleGroupIds(filteredRows.map((row) => row.id))
                                    } else {
                                      setSelectedVisibleGroupIds([])
                                    }
                                  }}
                                />
                              </td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Groups__Visible__ContainerTable__Table__TextColumn'>
                                Nombre
                              </td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Groups__Visible__ContainerTable__Table__TextColumn'>
                                Ztudiantes
                              </td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Groups__Visible__ContainerTable__Table__TextColumn'>
                                Código
                              </td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Groups__Visible__ContainerTable__Table__ActionButtonsColumn'></td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Groups__Visible__ContainerTable__Table__ActionButtonsColumn'></td>
                              <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Groups__Visible__ContainerTable__Table__ActionButtonsColumn'></td>
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {filteredRows.length > 0 ? (
                            filteredRows.map((row) => (
                              <tr key={row.id} className='Tables__Table__Row'>
                                <td className='Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Groups__Visible__ContainerTable__Table__InputColumn'>
                                  <input
                                    type='checkbox'
                                    className='Tables__Table__InputColumn__Input'
                                    checked={selectedVisibleGroupIds.includes(row.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedVisibleGroupIds((prev) => [...prev, row.id])
                                      } else {
                                        setSelectedVisibleGroupIds((prev) =>
                                          prev.filter((id) => id !== row.id)
                                        )
                                      }
                                    }}
                                  />
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Groups__Visible__ContainerTable__Table__TextColumn'>
                                  {row.name}
                                  {/* Botón para editar nombre del grupo (visibles) */}
                                  <button
                                    className='Btn__Ztudia--PrimaryOutline Instructor__Groups__Visible__ContainerTable__Table__TextColumn__EditButton'
                                    onClick={() => OpenEditGroupModal(row)}
                                  >
                                    <i className='bi bi-pencil'></i>
                                  </button>
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Groups__Visible__ContainerTable__Table__TextColumn'>
                                  {row.ztudiantesQuantity}
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Groups__Visible__ContainerTable__Table__TextColumn'>
                                  {row.accessCode}
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Groups__Visible__ContainerTable__Table__ActionButtonsColumn'>
                                  <button
                                    className='Btn__Ztudia--PrimaryOutline'
                                    onClick={() => ShowGroupDetails(row)}
                                  >
                                    Ver grupo
                                  </button>
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Groups__Visible__ContainerTable__Table__ActionButtonsColumn'>
                                  <button
                                    className='Btn__Ztudia--PrimaryOutline'
                                    onClick={() => ShowConfirmModal(actionType.HIDE, row)}
                                  >
                                    <i className='bi bi-eye-slash'></i> Ocultar
                                  </button>
                                </td>
                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Groups__Visible__ContainerTable__Table__ActionButtonsColumn'>
                                  <button
                                    className='Btn__Ztudia--PrimaryOutline'
                                    onClick={() => ShowConfirmModal(actionType.DELETE, row)}
                                  >
                                    <i className='bi bi-trash'></i> Eliminar
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr className='Tables__Table__Row Tables__Table__Row--Empty'>
                              <td className='Tables__Table__Row__Cell' colSpan={7}>
                                {searchTextVisible
                                  ? `No hay coincidencias para “${searchTextVisible}”.`
                                  : 'No hay grupos visibles que coincidan con el filtro.'}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <p className='Instructor__Text--Center'>
              No ha sido posible mostrar el listado de grupos. Intenta actualizar la página o inténtalo
              más tarde.
            </p>
          )}

          {/* ======== OCULTOS ======== */}
          {hiddenTotal > 0 && (
            <>
              <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>
                Grupos ocultos
              </p>
              <p className='Instructor__Subtitle Instructor__Subtitle--Center'>
                Total: {hiddenTotal}
              </p>

              {/* Buscador ocultos */}
              <Search onChange={(v) => setSearchTextHidden(v)} extraClassName='Instructor__SpaceUp' />

              <div className='container Instructor__SpaceUp Tables--Shadow Tables--Background'>
                {/* Acciones masivas (ocultos) */}
                {selectedHiddenGroupIds.length > 0 && (
                  <div className='Tables__ExtraButtons'>
                    <button
                      className='Btn__Ztudia--PrimaryOutline Tables__ExtraButtons__Button'
                      onClick={async () => {
                        await HandleMassiveAction({
                          selectedRows: GetSelectedHiddenGroups(),
                          actionFn: (group, visibleRowsList, hiddenRowsList) =>
                            BLShowGroup(group, visibleRowsList, hiddenRowsList),
                          actionLabel: actionType.SHOW
                        })
                      }}
                    >
                      Mostrar {selectedHiddenGroupIds.length} grupo(s)
                    </button>

                    <button
                      className='Btn__Ztudia--DangerOutline Tables__ExtraButtons__Button'
                      onClick={async () => {
                        await HandleMassiveAction({
                          selectedRows: GetSelectedHiddenGroups(),
                          actionFn: (group, rows) => BLDeleteGroup(group, rows),
                          actionLabel: actionType.DELETE
                        })
                      }}
                    >
                      Eliminar {selectedHiddenGroupIds.length} grupo(s)
                    </button>
                  </div>
                )}

                {/* Franja de seleccionados (ocultos) */}
                {selectedHiddenGroupIds.length > 0 && (
                  <div className='Tables__Table__RegistersSelected'>
                    <button
                      className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Sm'
                      onClick={() => setSelectedHiddenGroupIds([])}
                    >
                      <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                      <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                    </button>

                    <div className='Tables__Table__RegistersSelected__Text'>
                      {selectedHiddenGroupIds.length} grupo(s) seleccionado(s)
                    </div>

                    <button
                      className='Tables__Table__RegistersSelected__Button Tables__Table__RegistersSelected__Button--Lg'
                      onClick={() => setSelectedHiddenGroupIds([])}
                    >
                      <span className='Tables__Table__RegistersSelected__Button__Text'>X</span>
                      <span className='Tables__Table__RegistersSelected__Button__Text'>Cerrar</span>
                    </button>
                  </div>
                )}

                {/* Tabla de ocultos */}
                <div className='Tables__Container'>
                  <table className='Tables__Table Instructor__Groups__Hidden__ContainerTable__Table'>
                    <thead>
                      <tr className='Tables__Table__Row--Header'>
                        <th className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn Instructor__Groups__Hidden__ContainerTable__Table__InputColumn'>
                          <input
                            type='checkbox'
                            className='Tables__Table__InputColumn__Input'
                            checked={
                              filteredHiddenRows.length > 0 &&
                              selectedHiddenGroupIds.length === filteredHiddenRows.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedHiddenGroupIds(filteredHiddenRows.map((row) => row.id))
                              } else {
                                setSelectedHiddenGroupIds([])
                              }
                            }}
                          />
                        </th>
                        <th className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Groups__Hidden__ContainerTable__Table__TextColumn'>
                          Nombre
                        </th>
                        <th className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Groups__Hidden__ContainerTable__Table__TextColumn'>
                          Ztudiantes
                        </th>
                        <th className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Groups__Hidden__ContainerTable__Table__TextColumn'>
                          Código
                        </th>
                        <th className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Groups__Hidden__ContainerTable__Table__ActionButtonsColumn'></th>
                        <th className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Groups__Hidden__ContainerTable__Table__ActionButtonsColumn'></th>
                        <th className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn Instructor__Groups__Hidden__ContainerTable__Table__ActionButtonsColumn'></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHiddenRows.length > 0 ? (
                        filteredHiddenRows.map((row) => (
                          <tr key={row.id} className='Tables__Table__Row'>
                            <td className='Tables__Table__Row__Cell Tables__Table__InputColumn Instructor__Groups__Hidden__ContainerTable__Table__InputColumn'>
                              <input
                                type='checkbox'
                                className='Tables__Table__InputColumn__Input'
                                checked={selectedHiddenGroupIds.includes(row.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedHiddenGroupIds((prev) => [...prev, row.id])
                                  } else {
                                    setSelectedHiddenGroupIds((prev) =>
                                      prev.filter((id) => id !== row.id)
                                    )
                                  }
                                }}
                              />
                            </td>
                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Groups__Hidden__ContainerTable__Table__TextColumn'>
                              {row.name}
                              {/* Botón para editar nombre del grupo (ocultos) */}
                              <button
                                className='Btn__Ztudia--PrimaryOutline Instructor__Groups__Visible__ContainerTable__Table__TextColumn__EditButton'
                                onClick={() => OpenEditGroupModal(row)}
                              >
                                <i className='bi bi-pencil'></i>
                              </button>
                            </td>
                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Groups__Hidden__ContainerTable__Table__TextColumn'>
                              {row.ztudiantesQuantity}
                            </td>
                            <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Groups__Hidden__ContainerTable__Table__TextColumn'>
                              {row.accessCode}
                            </td>
                            <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Groups__Hidden__ContainerTable__Table__ActionButtonsColumn'>
                              <button
                                className='Btn__Ztudia--PrimaryOutline'
                                onClick={() => ShowGroupDetails(row)}
                              >
                                Ver grupo
                              </button>
                            </td>
                            <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Groups__Hidden__ContainerTable__Table__ActionButtonsColumn'>
                              <button
                                className='Btn__Ztudia--PrimaryOutline'
                                onClick={() => ShowConfirmModal(actionType.SHOW, row)}
                              >
                                <i className='bi bi-eye'></i> Mostrar
                              </button>
                            </td>
                            <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn Instructor__Groups__Hidden__ContainerTable__Table__ActionButtonsColumn'>
                              <button
                                className='Btn__Ztudia--PrimaryOutline'
                                onClick={() => ShowConfirmModal(actionType.DELETE, row)}
                              >
                                <i className='bi bi-trash'></i> Eliminar
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className='Tables__Table__Row Tables__Table__Row--Empty'>
                          <td className='Tables__Table__Row__Cell' colSpan={7}>
                            {searchTextHidden
                              ? `No hay coincidencias para “${searchTextHidden}”.`
                              : 'No hay grupos ocultos que coincidan con el filtro.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ===== Modal de edición de nombre de grupo ===== */}
          {showEditModal && editingGroup && (
            <Details
              stepButtons={false}
              showCloseModalButtonInContent={false}
              extraClassName={'Modal__Layout--Summary'}
              setShowModal={setShowEditModal}
              setDetailsData={setEditingGroup}
            >
              <div className='Modal__Layout--Summary__Container'>
                <p className='Modal__Layout--Summary__Title'>Editar nombre del grupo</p>

                <p className='Modal__Layout--Summary__Text'>Nombre actual:</p>
                <p className='Modal__Layout--Summary__Text'>{editingGroup.name}</p>

                <label className='Form__Label'>Nuevo nombre del grupo:</label>
                <input type='text' className='Form__Input Instructor__Groups__Modal--Edit__Input' value={editingName} onChange={(e) => setEditingName(e.target.value)} />

                <div className='Modal__Layout--Summary__Buttons Instructor__Groups__Modal--Edit__Buttons'>
                  <button
                    type='button'
                    className='Btn__Ztudia--PrimaryOutline Instructor__Groups__Modal--Edit__Buttons__Button'
                    disabled={
                      !editingName.trim() || editingName.trim() === editingGroup.name
                    }
                    onClick={() => {
                      const newName = editingName.trim()
                      if (!newName || newName === editingGroup.name) return

                      // Cerramos modal de edición
                      setShowEditModal(false)

                      // Guardamos datos (grupo + nombre nuevo) para confirmación
                      setGroupConfirmDetails({
                        ...editingGroup,
                        newName: newName
                      })

                      // Acción de renombrado
                      setActionTypeOfModal(actionType.RENAME)

                      // Abrimos modal de confirmación
                      setShowConfirmationModal(true)
                    }}
                  >
                    Guardar cambios
                  </button>

                  <button type='button' className='Btn__Ztudia--DangerOutline Instructor__Groups__Modal--Edit__Buttons__Button' onClick={() => setShowEditModal(false)}>Cancelar</button>
                </div>
              </div>
            </Details>
          )}

          {/* ===== Modales de confirmación ===== */}
          {showConfirmationModal && groupConfirmDetails && (
            actionTypeOfModal === actionType.DELETE ? (
              <Confirm
                message={`¿Estás segur@ de eliminar el grupo ${groupConfirmDetails?.name}?`}
                actionToYesButton={DeleteGroup}
                textYesButton={'Sí, eliminar grupo'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setGroupConfirmDetails}
              />
            ) : actionTypeOfModal === actionType.HIDE ? (
              <Confirm
                message={`¿Estás segur@ de ocultar el grupo ${groupConfirmDetails?.name}?`}
                actionToYesButton={HideGroup}
                textYesButton={'Sí, ocultar grupo'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setGroupConfirmDetails}
              />
            ) : actionTypeOfModal === actionType.SHOW ? (
              <Confirm
                message={`¿Estás segur@ de mostrar el grupo ${groupConfirmDetails?.name}?`}
                actionToYesButton={ShowGroup}
                textYesButton={'Sí, mostrar grupo'}
                textNoButton={'No'}
                setShowModal={setShowConfirmationModal}
                setConfirmData={setGroupConfirmDetails}
              />
            ) : (
              actionTypeOfModal === actionType.RENAME && (
                <Confirm
                  message={`¿Estás segur@ de cambiar el nombre del grupo "${groupConfirmDetails?.name}" a "${groupConfirmDetails?.newName}"?`}
                  actionToYesButton={RenameGroup}
                  textYesButton={'Sí, cambiar nombre ahora'}
                  textNoButton={'No'}
                  setShowModal={setShowConfirmationModal}
                  setConfirmData={setGroupConfirmDetails}
                />
              )
            )
          )}

          {/* ===== Modal de resumen para acciones masivas ===== */}
          {showMassiveSummaryModal && massiveSummary && (
            <Details
              stepButtons={false}
              showCloseModalButtonInContent={true}
              extraClassName={'Modal__Layout--Summary'}
              setShowModal={setShowMassiveSummaryModal}
              setDetailsData={setMassiveSummary}
            >
              <div className='Modal__Layout--Summary__Container'>
                <p className='Modal__Layout--Summary__Title'>
                  Resumen{' '}
                  {actionTypeOfModal === actionType.DELETE
                    ? 'de grupos eliminados'
                    : actionTypeOfModal === actionType.HIDE
                    ? 'de grupos ocultos'
                    : actionTypeOfModal === actionType.SHOW && 'de grupos mostrados'}
                </p>

                {massiveSummary ? (
                  <>
                    {massiveSummary.successful.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-check-circle'></i>{' '}
                          {actionTypeOfModal === actionType.DELETE
                            ? 'Grupos eliminados:'
                            : actionTypeOfModal === actionType.HIDE
                            ? 'Grupos ocultos:'
                            : actionTypeOfModal === actionType.SHOW && 'Grupos mostrados:'}
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.successful.map((success, index) => (
                            <p key={index} className='Modal__Layout--Summary__TextContainer__Text'>{success.name}</p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.wrongs.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'>
                          <i className='bi bi-x-circle'></i>{' '}
                          {actionTypeOfModal === actionType.DELETE
                            ? 'Error al eliminarlo(s):'
                            : actionTypeOfModal === actionType.HIDE
                            ? 'Error al ocultarlo(s):'
                            : actionTypeOfModal === actionType.SHOW && 'Error al mostrarlo(s):'}
                        </p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.wrongs.map((wrong, index) => (
                            <p key={index} className='Modal__Layout--Summary__TextContainer__Text'>{wrong.name}</p>
                          ))}
                        </div>
                      </>
                    )}

                    {massiveSummary.omitteds.length > 0 && (
                      <>
                        <p className='Modal__Layout--Summary__Category'><i className='bi bi-skip-end-btn'></i> Omitidos:</p>
                        <div className='Modal__Layout--Summary__TextContainer'>
                          {massiveSummary.omitteds.map((omitted, index) => (
                            <p key={index} className='Modal__Layout--Summary__TextContainer__Text'>{omitted.name}</p>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <p className='Modal__Layout--Summary__Text'>No ha sido posible mostrar el resumen de lo realizado.</p>
                )}
              </div>
            </Details>
          )}
        </section>
      </InstructorBase>
    </>
  )
}

export { Groups };